import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { 
  MdArrowBack, MdStar, MdLocationOn, MdAccessTime, MdPerson, 
  MdSchool, MdLanguage, MdDriveEta, MdSmokingRooms, MdHome,
  MdCheckCircle, MdVerified, MdFavorite, MdShare, MdPhone,
  MdEmail, MdCake, MdWork, MdFavoriteBorder, MdTranslate
} from 'react-icons/md';
import api from '../../services/api';
import { getUser } from '../../services/auth';
import { t } from '../../utils/i18n';

// Helper function to get avatar URL from UI Avatars API
const getAvatarUrl = (name, size = 200) => {
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&size=${size}&background=9333ea&color=fff&bold=true`;
};

// Helper function to format last activity
const formatLastActivity = (timestamp) => {
  if (!timestamp) return 'Never';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
};

// Helper function to get day name
const getDayName = (day) => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  return days[day] || '';
};

// Helper function to get time slot label
const getTimeSlotLabel = (slot) => {
  const labels = {
    morning: 'Morning',
    afternoon: 'Afternoon',
    evening: 'Evening',
    night: 'Night'
  };
  return labels[slot] || slot;
};

export default function BabysitterProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = getUser();
  const isPublicProfile = location.pathname.startsWith('/babysitter/') && !location.pathname.startsWith('/dashboard');
  const [babysitter, setBabysitter] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [references, setReferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    loadBabysitterProfile();
  }, [id]);

  const loadBabysitterProfile = async () => {
    try {
      setLoading(true);
      // Prvo pokušaj /users/providers/:id (eksplicitno za provider), pa fallback na /users/:id
      let response = await api.get(`/users/providers/${id}`).catch(() => ({ data: null }));
      if (!response?.data) {
        response = await api.get(`/users/${id}`).catch(() => ({ data: null }));
      }
      
      if (response?.data) {
        setBabysitter(response.data);
        
        if (response.data.availability) {
          setAvailability(response.data.availability);
        }
        
        loadReferences(id);
      }
    } catch (error) {
      console.error('Error loading babysitter profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReferences = async (babysitterId) => {
    try {
      const response = await api.get(`/users/${babysitterId}/references`).catch(() => ({ data: [] }));
      setReferences(response.data || []);
    } catch (error) {
      console.error('Error loading references:', error);
    }
  };

  const [showContactLoginMessage, setShowContactLoginMessage] = useState(false);

  const handleContact = () => {
    if (!currentUser) {
      setShowContactLoginMessage(true);
      return;
    }
    navigate(`/dashboard/conversations?user=${id}`);
  };

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite);
    // TODO: Implement favorite functionality
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!babysitter) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Babysitter not found</p>
      </div>
    );
  }

  const fullName = `${babysitter.firstName || ''} ${babysitter.lastName || ''}`.trim();
  const avatarUrl = babysitter.photoUrl || getAvatarUrl(fullName);
  const characteristics = babysitter.characteristics || [];
  const superpowers = babysitter.superpowers || [];
  const languages = babysitter.languages || [];
  const experienceAges = babysitter.experienceAges || [];

  return (
    <div className="h-full overflow-y-auto min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Javni header kada se stranica vidi bez prijave */}
      {isPublicProfile && (
        <header className="sticky top-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/" className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700">
                <MdArrowBack className="text-xl" />
              </Link>
              <Link to="/" className="font-semibold text-gray-900 dark:text-gray-100">Nannity</Link>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/login" className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 font-medium">
                Prijava
              </Link>
              <Link to="/register" className="px-4 py-2 rounded-full bg-primary-600 text-white font-medium hover:bg-primary-700">
                Registracija
              </Link>
            </div>
          </div>
        </header>
      )}
      <div className="max-w-4xl mx-auto p-4 lg:p-6 space-y-6">
        {/* Header (unutar dashboarda) */}
        {!isPublicProfile && (
        <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <MdArrowBack className="text-2xl" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex-1">
              {t('babysitter.profile') || 'Babysitter Profile'}
            </h1>
            <div className="flex gap-2">
              <button
                onClick={toggleFavorite}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {isFavorite ? (
                  <MdFavorite className="text-2xl text-pink-500" />
                ) : (
                  <MdFavoriteBorder className="text-2xl" />
                )}
              </button>
              <button
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <MdShare className="text-2xl" />
              </button>
            </div>
        </div>
        )}

        {/* Profile Header Card */}
        <div className="card p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <img
                    src={avatarUrl}
                    alt={fullName}
                    className="w-32 h-32 rounded-full object-cover border-4 border-primary-200 dark:border-primary-800"
                  />
                  {babysitter.verified && (
                    <div className="absolute bottom-0 right-0 bg-pink-500 rounded-full p-1">
                      <MdVerified className="text-white text-xl" />
                    </div>
                  )}
                </div>
              </div>

              {/* Basic Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {fullName}
                  </h2>
                  {babysitter.verified && (
                    <MdCheckCircle className="text-pink-500 text-2xl" />
                  )}
                </div>
                
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-4">
                  {babysitter.role === 'babysitter' ? 'Babysitter' : 'Nanny'} in {babysitter.address?.city || 'Belgrade'}
                </p>

                {/* Stats Row */}
                <div className="flex flex-wrap gap-4 mb-4">
                  {babysitter.age && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <MdCake />
                      <span>Age: {babysitter.age}</span>
                    </div>
                  )}
                  {babysitter.distanceKm && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <MdLocationOn />
                      <span>Distance: ~{babysitter.distanceKm}km</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                    <MdAccessTime />
                    <span>Hourly rate: RSD{babysitter.hourlyRate || 0}/hr</span>
                  </div>
                </div>

                {/* Rating */}
                {babysitter.rating?.average && (
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <MdStar
                          key={i}
                          className={`text-xl ${
                            i < Math.floor(babysitter.rating.average)
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-gray-600 dark:text-gray-400">
                      ({babysitter.rating.count || 0})
                    </span>
                  </div>
                )}

                {/* References and Activity */}
                <div className="flex flex-wrap gap-4 text-sm">
                  {babysitter.referencesCount > 0 && (
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                      <MdCheckCircle />
                      <span>{babysitter.referencesCount} Reference{babysitter.referencesCount > 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {babysitter.lastActivity && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <MdAccessTime />
                      <span>Last activity: {formatLastActivity(babysitter.lastActivity)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bio Section */}
          {babysitter.bio && (
            <div className="card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {t('babysitter.about') || 'About'}
                </h3>
                {babysitter.bioTranslated && (
                  <button className="flex items-center gap-2 text-primary-600 dark:text-primary-400">
                    <MdTranslate />
                    <span className="text-sm">Translate</span>
                  </button>
                )}
              </div>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                {babysitter.bio}
              </p>
            </div>
          )}

          {/* Characteristics */}
          {characteristics.length > 0 && (
            <div className="card p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                {t('babysitter.characteristics') || 'Characteristics'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {characteristics.map((char, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 rounded-full text-sm font-medium"
                  >
                    {char}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Experience */}
          <div className="card p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {t('babysitter.experience') || 'Experience'}
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <MdWork className="text-primary-600 dark:text-primary-400 text-xl" />
                <span className="text-gray-700 dark:text-gray-300">
                  Babysitting experience: {babysitter.experienceYears != null ? `>${babysitter.experienceYears} years` : 'Nema prethodnog iskustva'}
                </span>
              </div>
              {experienceAges.length > 0 && (
                <div className="flex items-center gap-3">
                  <MdCake className="text-primary-600 dark:text-primary-400 text-xl" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Experience with age(s): {experienceAges.join(' · ')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Verification */}
          {(babysitter.governmentIdVerified || babysitter.photoVerified) && (
            <div className="card p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
              <div className="flex items-start gap-3">
                <MdCheckCircle className="text-green-600 dark:text-green-400 text-2xl flex-shrink-0 mt-1" />
                <div>
                  <p className="text-green-800 dark:text-green-300 font-medium mb-1">
                    {fullName} successfully provided a government ID and passed photo verification checks.
                  </p>
                  <a href="#" className="text-green-600 dark:text-green-400 text-sm underline">
                    Learn more
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Availability */}
          {availability.length > 0 && (
            <div className="card p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                {t('babysitter.availability') || 'Availability'}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="text-left p-2"></th>
                      <th className="text-center p-2 text-sm">Morning</th>
                      <th className="text-center p-2 text-sm">Afternoon</th>
                      <th className="text-center p-2 text-sm">Evening</th>
                      <th className="text-center p-2 text-sm">Night</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[0, 1, 2, 3, 4, 5, 6].map(day => (
                      <tr key={day} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="p-2 font-medium text-gray-700 dark:text-gray-300">
                          {getDayName(day).slice(0, 2)}
                        </td>
                        {['morning', 'afternoon', 'evening', 'night'].map(slot => {
                          const isAvailable = availability.some(
                            a => a.day === day && a.slot === slot && a.available
                          );
                          return (
                            <td key={slot} className="p-2 text-center">
                              {isAvailable ? (
                                <MdCheckCircle className="text-green-500 text-xl mx-auto" />
                              ) : (
                                <span className="text-gray-300 dark:text-gray-600">—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-4 flex items-center gap-2">
                <MdAccessTime />
                Updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          )}

          {/* About Me */}
          <div className="card p-6">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {t('babysitter.aboutMe') || 'About Me'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <MdDriveEta className="text-primary-600 dark:text-primary-400" />
                <span className="text-gray-700 dark:text-gray-300">
                  Driving license: {babysitter.drivingLicense ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <MdDriveEta className="text-primary-600 dark:text-primary-400" />
                <span className="text-gray-700 dark:text-gray-300">
                  Car: {babysitter.hasCar ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <MdPerson className="text-primary-600 dark:text-primary-400" />
                <span className="text-gray-700 dark:text-gray-300">
                  Has children: {babysitter.hasChildren ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <MdSmokingRooms className="text-primary-600 dark:text-primary-400" />
                <span className="text-gray-700 dark:text-gray-300">
                  Smoker: {babysitter.smoker ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <MdHome className="text-primary-600 dark:text-primary-400" />
                <span className="text-gray-700 dark:text-gray-300">
                  Preferred location: {babysitter.preferredLocation === 'at_family' ? 'At the family' : 
                                      babysitter.preferredLocation === 'at_my_place' ? 'At my place' : 'Both'}
                </span>
              </div>
              {languages.length > 0 && (
                <div className="flex items-center gap-3">
                  <MdLanguage className="text-primary-600 dark:text-primary-400" />
                  <span className="text-gray-700 dark:text-gray-300">
                    Languages: {languages.join(', ')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Education */}
          {(babysitter.educationLevel || babysitter.educationDetails) && (
            <div className="card p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                {t('babysitter.education') || 'Education'}
              </h3>
              <div className="space-y-2">
                {babysitter.educationLevel && (
                  <div className="flex items-center gap-3">
                    <MdSchool className="text-primary-600 dark:text-primary-400" />
                    <span className="text-gray-700 dark:text-gray-300">
                      Education level: {babysitter.educationLevel}
                    </span>
                  </div>
                )}
                {babysitter.educationDetails && (
                  <div className="flex items-center gap-3">
                    <MdSchool className="text-primary-600 dark:text-primary-400" />
                    <span className="text-gray-700 dark:text-gray-300">
                      {babysitter.educationDetails}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Superpowers */}
          {superpowers.length > 0 && (
            <div className="card p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                {t('babysitter.superpowers') || 'My babysitting superpowers'}
              </h3>
              <div className="flex flex-wrap gap-4">
                {superpowers.map((power, index) => (
                  <div key={index} className="flex items-center gap-2 px-4 py-2 bg-primary-50 dark:bg-primary-900/30 rounded-lg">
                    <span className="text-primary-700 dark:text-primary-300">{power}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Comfortable With */}
          {babysitter.comfortableWith && babysitter.comfortableWith.length > 0 && (
            <div className="card p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                {t('babysitter.comfortableWith') || 'Comfortable with'}
              </h3>
              <div className="flex flex-wrap gap-4">
                {babysitter.comfortableWith.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/30 rounded-lg">
                    <span className="text-green-700 dark:text-green-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* References */}
          {references.length > 0 && (
            <div className="card p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                {t('babysitter.references') || 'References'}
              </h3>
              <div className="space-y-4">
                {references.map((ref) => (
                  <div key={ref.id} className="border-l-4 border-primary-500 pl-4">
                    <div className="flex items-center gap-2 mb-2">
                      {[...Array(5)].map((_, i) => (
                        <MdStar
                          key={i}
                          className={`text-sm ${
                            i < ref.rating
                              ? 'text-yellow-400 fill-current'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                      {ref.verified && (
                        <MdVerified className="text-green-500 text-sm" />
                      )}
                    </div>
                    <p className="text-gray-700 dark:text-gray-300">{ref.referenceText}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      {new Date(ref.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Poruka: morate da se ulogujete da biste razmenjivali poruke */}
        {showContactLoginMessage && (
          <div className="rounded-xl bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 p-4 mb-4">
            <p className="text-primary-800 dark:text-primary-200 font-medium mb-3">
              Morate da se ulogujete da biste razmenjivali poruke sa korisnikom.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => navigate('/login', { state: { from: `/babysitter/${id}` } })}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700"
              >
                Prijava
              </button>
              <button
                type="button"
                onClick={() => setShowContactLoginMessage(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Zatvori
              </button>
            </div>
          </div>
        )}

        {/* Contact Button — vidljiv i gostima; klik bez prijave prikaže poruku iznad */}
        {(currentUser?.role === 'parent' || isPublicProfile) && (
          <div className="sticky bottom-0 p-4 -mx-4 lg:-mx-6">
            <button
              onClick={handleContact}
              className="w-full btn-primary flex items-center justify-center gap-2 py-4 text-lg"
            >
              <MdEmail />
              {t('babysitter.contact') || 'Kontaktiraj'} {babysitter.firstName}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

